import { onScopeDispose, ref, watch, type Ref } from 'vue';

/**
 * Tunable timings for the global progress bar. Exposed so the spec can shrink
 * them and assert on exact frames with fake timers.
 */
export interface ProgressTimings {
  /** Wait this long before showing the bar — sub-threshold requests never flash it. */
  debounceMs: number;
  /** Once shown, keep the bar up at least this long so fast bursts don't strobe. */
  minVisibleMs: number;
  /** Interval between trickle ticks while active. */
  trickleMs: number;
}

export const DEFAULT_TIMINGS: ProgressTimings = {
  debounceMs: 180,
  minVisibleMs: 350,
  trickleMs: 220,
};

export interface ProgressController {
  /** Whether the bar should be in the DOM / visible. */
  visible: Ref<boolean>;
  /** Current fill, 0..1. Trickles toward ~0.9 while active; snaps to 1 on settle. */
  progress: Ref<number>;
}

/**
 * The internal timer surface. Defaults to the real platform timers; the spec
 * injects fakes (or relies on vitest's `vi.useFakeTimers()` swapping globals,
 * which these references pick up at call time).
 */
interface TimerApi {
  setTimeout: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (h: ReturnType<typeof setTimeout>) => void;
  now: () => number;
}

const realTimers: TimerApi = {
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h),
  now: () => Date.now(),
};

/**
 * Pure, ref-driven controller for an NProgress / YouTube-style top bar. Given a
 * boolean `isActive` source (true while any request is in flight), it returns
 * `{ visible, progress }` refs the component binds to. All timing lives here so
 * the debounce / trickle / min-visible / reduced-motion behaviour is unit-test-
 * able without mounting Vue (drive `isActive` + advance fake timers).
 *
 * Behaviour:
 * - **Debounce show:** going active starts a `debounceMs` timer; only after it
 *   elapses (still active) does the bar appear. A burst that settles inside the
 *   window never shows.
 * - **Trickle:** while visible + active, `progress` eases toward (but never
 *   reaches) ~0.9 on each `trickleMs` tick. Reduced-motion skips the trickle and
 *   shows a steady indeterminate fill instead.
 * - **Min-visible:** once shown, the bar stays up for at least `minVisibleMs`
 *   even if activity settles immediately — prevents flicker on quick bursts.
 * - **Settle:** when activity ends (and min-visible has elapsed), `progress`
 *   snaps to 1 then the bar hides on the next tick, resetting to 0.
 * - **Coalesce:** active → inactive → active inside the windows is treated as one
 *   continuous activity (no hide/reshow churn).
 *
 * @param isActive Reactive source: true while ≥1 request is in flight.
 * @param opts Optional overrides for timings, reduced-motion, and timer injection.
 * @returns `{ visible, progress }` refs for the component to bind.
 */
export function useProgressController(
  isActive: Ref<boolean>,
  opts?: {
    timings?: Partial<ProgressTimings>;
    /**
     * When true, no trickle animation — a steady indeterminate fill + plain
     * show/hide. Pass a getter (`() => mq.matches`) to re-evaluate on each
     * show so a mid-session OS toggle is honored.
     */
    reducedMotion?: boolean | (() => boolean);
    /** Injectable timers for deterministic specs; defaults to platform timers. */
    timers?: TimerApi;
  },
): ProgressController {
  const timings: ProgressTimings = { ...DEFAULT_TIMINGS, ...(opts?.timings ?? {}) };
  const reducedMotionOpt = opts?.reducedMotion ?? false;
  /** Read the current reduced-motion preference (re-evaluated each show). */
  const reducedMotionNow = (): boolean =>
    typeof reducedMotionOpt === 'function' ? reducedMotionOpt() : reducedMotionOpt;
  const timers = opts?.timers ?? realTimers;

  const visible = ref(false);
  // Reduced-motion uses a steady "indeterminate" fill; full-motion trickles from 0.
  const progress = ref(0);

  let debounceHandle: ReturnType<typeof setTimeout> | null = null;
  let trickleHandle: ReturnType<typeof setTimeout> | null = null;
  let hideHandle: ReturnType<typeof setTimeout> | null = null;
  // Wall-clock time the bar became visible — gates the min-visible window.
  let shownAt = 0;

  function clearDebounce(): void {
    if (debounceHandle !== null) {
      timers.clearTimeout(debounceHandle);
      debounceHandle = null;
    }
  }
  function clearTrickle(): void {
    if (trickleHandle !== null) {
      timers.clearTimeout(trickleHandle);
      trickleHandle = null;
    }
  }
  function clearHide(): void {
    if (hideHandle !== null) {
      timers.clearTimeout(hideHandle);
      hideHandle = null;
    }
  }

  function scheduleTrickle(): void {
    if (reducedMotionNow()) return; // steady fill — no animation
    clearTrickle();
    trickleHandle = timers.setTimeout(() => {
      // Ease toward 0.9 without ever reaching it: add a shrinking fraction of
      // the remaining distance to 0.9. Classic NProgress trickle.
      const remaining = 0.9 - progress.value;
      if (remaining > 0.001) {
        progress.value = Math.min(0.9, progress.value + remaining * 0.25);
      }
      // Keep ticking while still active + visible.
      if (isActive.value && visible.value) scheduleTrickle();
    }, timings.trickleMs);
  }

  function show(): void {
    if (visible.value) return;
    visible.value = true;
    shownAt = timers.now();
    progress.value = reducedMotionNow() ? 1 : 0.08; // small initial jump so it reads instantly
    scheduleTrickle();
  }

  function finishAndHide(): void {
    clearTrickle();
    // Snap to full, then hide + reset. The component's CSS handles the fade.
    progress.value = 1;
    visible.value = false;
    progress.value = 0;
    shownAt = 0;
  }

  /** Settle: only hide once the min-visible window has elapsed. */
  function settle(): void {
    clearHide();
    const elapsed = timers.now() - shownAt;
    const wait = Math.max(0, timings.minVisibleMs - elapsed);
    if (wait === 0) {
      finishAndHide();
      return;
    }
    hideHandle = timers.setTimeout(() => {
      hideHandle = null;
      // Re-check: activity may have resumed during the min-visible wait —
      // if so, stay visible and keep trickling (coalesced as one activity).
      if (isActive.value) {
        scheduleTrickle();
      } else {
        finishAndHide();
      }
    }, wait);
  }

  watch(
    isActive,
    (active) => {
      if (active) {
        // Activity resumed before we hid → cancel any pending hide and keep going.
        clearHide();
        if (visible.value) {
          scheduleTrickle();
          return;
        }
        // Not yet visible → (re)arm the debounce.
        if (debounceHandle === null) {
          debounceHandle = timers.setTimeout(() => {
            debounceHandle = null;
            if (isActive.value) show();
          }, timings.debounceMs);
        }
      } else {
        // Went idle. If the debounce hasn't fired, cancel it — never showed.
        clearDebounce();
        if (visible.value) settle();
      }
    },
    // Sync flush: the activity flag flips outside Vue's render cycle (axios
    // interceptors), and the timing logic must arm its timers the instant the
    // flag changes — not on the next scheduler tick. Sync keeps the bar's
    // debounce honest and makes the controller deterministic under fake timers.
    { immediate: true, flush: 'sync' },
  );

  onScopeDispose(() => {
    clearDebounce();
    clearTrickle();
    clearHide();
  });

  return { visible, progress };
}
